// Copy for the T0 tutorial guide. Content generated from .codex-run/TUTORIAL-STEPS.json;
// do not invent customer names, metrics or invented records here.
export interface StepCopy {
  /** 1–2 plain-English sentences, max 220 chars: what this area does, why it matters, what the user should see after. */
  body: string
  /** Action steps (completion.event !== 'viewed'): an imperative telling the user what real thing to do, max 70 chars, e.g. 'Sign in with your email and password'. Info steps (event 'viewed'): ''. */
  action: string
  /** One sentence, max 140 chars: what Continue without doing it means, e.g. 'Continue to read on without adding a source. Nothing is changed.' For info steps: a short line like 'Read this, then press Next.' */
  readOnly: string
  /** One sentence, max 140 chars, step-specific: how to recover if the control is missing or the save failed (e.g. 'If the save fails, your text stays in the form. Fix the highlighted field and save again.'). */
  recovery: string
}

export const CHAPTER_TITLES: Record<string, string> = {
  T01: "Welcome, account and privacy",
  T02: "Your workspace",
  T03: "Research and sources",
  T04: "Products and people",
  T05: "Brand voice and appearance",
  T06: "Goals and plan",
  T07: "Home and navigation",
  T08: "Create",
  T09: "Understand your results",
  T10: "Studio: text and revisions",
  T11: "Studio: media",
  T12: "Content and reuse",
  T13: "Review and Autopilot",
  T14: "Calendar and delivery",
  T15: "Company knowledge and learning",
  T16: "Analytics and case studies",
  T17: "Account and connections",
  T18: "Finish and return",
}

export const STEP_COPY: Record<string, StepCopy> = {
  'T01.01': {
    body: "The sign-in form checks your email and password against your ZUPA account. A successful sign-in takes you to your workspace home.",
    action: "Sign in with your email and password",
    readOnly: "Continue without signing in to keep reading; nothing is submitted.",
    recovery: "If sign-in fails, the form stays filled in and shows the error so you can fix and retry.",
  },
  'T01.02': {
    body: "Verifying your email confirms the address is really yours and unlocks full account access. The status here updates once confirmed.",
    action: "Open the verification link sent to your email",
    readOnly: "Continue without verifying now; you can finish it later from this same screen.",
    recovery: "If the link expired, use resend on this screen and check your inbox again.",
  },
  'T01.03': {
    body: "Your privacy choices control what ZUPA is allowed to use and store for you. Confirming here saves your decision to your account.",
    action: "Review and confirm your privacy choices",
    readOnly: "Continue without changing anything; your existing choices stay in effect.",
    recovery: "If saving fails, your selections stay on screen so you can try confirming again.",
  },
  'T02.01': {
    body: "A workspace is the company or individual profile ZUPA writes for. Choosing company or individual and entering details creates it.",
    action: "Choose company or individual and enter your details",
    readOnly: "Continue without creating a workspace now; you can set one up later.",
    recovery: "If save fails, your entries stay in the form so you can fix a field and save again.",
  },
  'T02.02': {
    body: "The workspace switcher shows every workspace you belong to and marks which one is active. Everything you do applies to that one.",
    action: "Open the switcher and select your active workspace",
    readOnly: "Continue without switching; your current active workspace stays selected.",
    recovery: "If the switch does not take effect, reopen the switcher and select the workspace again.",
  },
  'T03.01': {
    body: "Sources are the websites or documents ZUPA reads to learn about your company. Adding one starts research that later suggests facts.",
    action: "Add a website URL or upload a document as a source",
    readOnly: "Continue without adding a source; you can add one from Company later.",
    recovery: "If the add fails, the field keeps your input so you can correct it and try again.",
  },
  'T03.02': {
    body: "After you add a source, ZUPA analyses it in the background. This progress indicator shows when that analysis has finished.",
    action: "Wait for the source analysis to finish",
    readOnly: "Continue without waiting for this analysis; it keeps running in the background.",
    recovery: "If analysis fails or stalls, retry from the source list or exit and check back later.",
  },
  'T03.03': {
    body: "The diff shows exactly which facts ZUPA proposes to add or change, and where each one came from. Nothing changes until you choose.",
    action: "Review the proposed changes and their source",
    readOnly: "Continue without reviewing in detail; no changes are applied by looking.",
    recovery: "If the diff will not load, retry loading it or skip this step and revisit it later.",
  },
  'T03.04': {
    body: "Applying only writes the specific facts you selected into your company profile. Anything left unchecked is discarded, not saved.",
    action: "Select the facts you want and apply them",
    readOnly: "Continue without applying anything; the proposed changes stay unapplied.",
    recovery: "If applying fails, your selections stay checked so you can retry the save.",
  },
  'T04.01': {
    body: "This list holds every product or service ZUPA can write about for you. Open an existing entry or add a new one from here.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If the list looks empty, refresh the page or add your first product from here.",
  },
  'T04.02': {
    body: "The product form captures the offer, who it is for and the call to action ZUPA should use in posts about it. Saving stores it.",
    action: "Describe the offer, audience and call to action, then save",
    readOnly: "Continue without saving a product now; you can add one later.",
    recovery: "If save fails, your text stays in the form. Fix the highlighted field and save again.",
  },
  'T04.03': {
    body: "This list holds the audiences ZUPA writes to. Each persona describes a group of people your content should speak to.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If the list looks empty, refresh the page or add your first persona from here.",
  },
  'T04.04': {
    body: "Editing a persona sets its buying context and marks whether it is your primary audience. ZUPA favours the primary one.",
    action: "Edit the buying context and choose the primary audience, then save",
    readOnly: "Continue without saving changes; the persona keeps its current details.",
    recovery: "If save fails, your edits stay in the form. Fix the highlighted field and save again.",
  },
  'T05.01': {
    body: "Voice settings and writing examples teach ZUPA how your company sounds, so generated posts match your tone, not a generic one.",
    action: "Set your voice and add writing examples, then save",
    readOnly: "Continue without saving voice settings now; a default voice is used until you do.",
    recovery: "If save fails, your text stays in the form. Fix the highlighted field and save again.",
  },
  'T05.02': {
    body: "Your logo, colours and visual style are used across image generation and overlays so visuals stay consistent with your brand.",
    action: "Set your logo, colours and visual style, then save",
    readOnly: "Continue without saving visual defaults now; you can set them later.",
    recovery: "If save fails, your selections stay in the form. Fix the highlighted field and save again.",
  },
  'T05.03': {
    body: "This reload confirms your saved brand actually persisted on the server, not just in the form you were looking at.",
    action: "Reload the page and confirm your saved brand appears",
    readOnly: "Continue without reloading; your save from the previous step still applies.",
    recovery: "If the reloaded brand does not match, open the form again and save once more.",
  },
  'T06.01': {
    body: "Marketing objectives and content priorities tell ZUPA what your posts should achieve, shaping the ideas and angles it suggests.",
    action: "Set your objectives and content priorities, then save",
    readOnly: "Continue without saving a strategy now; you can set one later.",
    recovery: "If save fails, your entries stay in the form. Fix the highlighted field and save again.",
  },
  'T06.02': {
    body: "Cadence and platform choices control how often and where ZUPA plans content for you. Saving updates your posting schedule.",
    action: "Choose your posting cadence and platforms, then save",
    readOnly: "Continue without saving cadence now; your current settings stay in effect.",
    recovery: "If save fails, your selections stay in the form. Fix the highlighted field and save again.",
  },
  'T06.03': {
    body: "Autopilot generates content on your cadence automatically. Pausing it stops new generation without deleting anything created.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If the toggle does not respond, refresh the page and try again from Company settings.",
  },
  'T07.01': {
    body: "Home shows what needs your attention, what is coming up next, and any drafts still in progress, for the active workspace.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If Home looks empty, that means nothing is pending yet; create a post to see it populate.",
  },
  'T07.02': {
    body: "The main navigation reaches every area of ZUPA: Home, Create, Content and Company on desktop, with Company folded under More on mobile.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If a nav item is missing on mobile, open More to find it there.",
  },
  'T07.03': {
    body: "The suggested next task points you at whatever is most useful right now, such as finishing setup or reviewing a draft.",
    action: "Open the suggested next task",
    readOnly: "Continue without opening it; reach the same area from the main navigation any time.",
    recovery: "If the suggestion will not open, use the main navigation to reach that area directly.",
  },
  'T08.01': {
    body: "Saved ideas are prompts you kept for later. Selecting one loads it straight into the Create brief so you can generate from it.",
    action: "Select a saved idea to load it into Create",
    readOnly: "Continue without selecting a saved idea; you can start Create with a new idea instead.",
    recovery: "If a saved idea will not load, refresh Create and select it again.",
  },
  'T08.02': {
    body: "The brief starts from your idea and lets you inspect what will be sent to generation, with an optional general audience if no persona fits.",
    action: "Enter your idea and review the brief",
    readOnly: "Continue without filling in a brief now; nothing is generated until you do.",
    recovery: "If the brief will not open, refresh the Create page and try again.",
  },
  'T08.03': {
    body: "These options choose which platforms ZUPA writes for, and set the tone and language of the generated post.",
    action: "Choose platforms, tone and language",
    readOnly: "Continue without setting options now; generation uses defaults until you choose.",
    recovery: "If an option will not save, reopen this panel and select it again before generating.",
  },
  'T08.04': {
    body: "These choices decide whether the post gets a single image, a carousel of slides or a thread, before you generate.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If the media options panel will not open, refresh Create and expand it again.",
  },
  'T08.05': {
    body: "Generating spends from your plan usage and produces real post options. The guide never generates for you; you press Generate yourself.",
    action: "Press Generate only if you want to use generation quota now",
    readOnly: "Continue without generating; no quota is used and no content is created.",
    recovery: "If generation fails, your brief stays filled in so you can try again.",
  },
  'T09.01': {
    body: "Each generated variant is written for a specific platform. Comparing them lets you pick which version to take into Studio.",
    action: "Compare the variants and select one",
    readOnly: "Continue without selecting a variant; the results stay available to open later.",
    recovery: "If a variant will not open, refresh the results and select it again.",
  },
  'T09.02': {
    body: "The reasoning panel explains why ZUPA wrote what it did and flags any warnings, such as a claim it could not verify.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If the reasoning panel is missing, it may be collapsed; expand the result to see it.",
  },
  'T09.03': {
    body: "Saving keeps the whole generated group on the server, not just in your browser. Reopening it confirms the save persisted.",
    action: "Save the draft group, then reopen it to confirm",
    readOnly: "Continue without saving now; unsaved results are lost if you navigate away.",
    recovery: "If the save fails, retry it before leaving the page or your results will not persist.",
  },
  'T10.01': {
    body: "Studio is where you edit the caption text of the selected post directly. Saving creates a new revision without touching the original.",
    action: "Edit the caption text, then save",
    readOnly: "Continue without editing; the current caption stays as generated.",
    recovery: "If save fails, your edited text stays in the editor so you can try saving again.",
  },
  'T10.02': {
    body: "Refine scope limits exactly what the AI may change, such as only the hook or only the length, before it proposes a rewrite.",
    action: "Choose what the AI may change, then request a refinement",
    readOnly: "Continue without requesting a refinement; your current text is untouched.",
    recovery: "If the refinement request fails, nothing is changed and you can try again.",
  },
  'T10.03': {
    body: "The AI proposal sits side by side with your current text. Keeping it replaces your text; discarding it leaves the original untouched.",
    action: "Compare the proposal, then keep or discard it",
    readOnly: "Continue without resolving the proposal now; it stays pending until you decide.",
    recovery: "If keep or discard fails to save, the proposal stays visible so you can try again.",
  },
  'T10.04': {
    body: "The version list holds every earlier revision. Restoring one creates a new revision from it; it never deletes versions in between.",
    action: "Pick an earlier version and restore it",
    readOnly: "Continue without restoring anything; your current text stays as is.",
    recovery: "If restore fails, your current revision is unchanged and you can try again.",
  },
  'T10.05': {
    body: "This status shows whether your edit is saving, saved, or failed to save, so you always know if your latest change persisted.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If the status is stuck on saving, wait briefly then check for a failed indicator and retry.",
  },
  'T11.01': {
    body: "This is where you attach an image to the post or replace the one already there. Saving stores the image on the revision.",
    action: "Add or replace the image, then save",
    readOnly: "Continue without adding an image; the post can still be saved without one.",
    recovery: "If the upload fails, the previous image stays attached and you can try uploading again.",
  },
  'T11.02': {
    body: "Generating an image spends from your plan usage, the same as text generation. The guide never generates an image for you; you press the button yourself.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If a generation attempt fails, your usage is not spent and you can retry.",
  },
  'T11.03': {
    body: "Image history keeps every version you generated or uploaded for this post. Reopening one makes it the active image again.",
    action: "Open an earlier image version to restore it",
    readOnly: "Continue without restoring an earlier image; the current image stays active.",
    recovery: "If restore fails, the current image stays unchanged and you can try again.",
  },
  'T11.04': {
    body: "These controls set the image aspect ratio, colour palette and whether your company logo appears on the image.",
    action: "Choose aspect, palette and logo, then save",
    readOnly: "Continue without changing visual controls; current defaults stay in use.",
    recovery: "If save fails, your selections stay on screen so you can try saving again.",
  },
  'T11.05': {
    body: "Overlay tools place and style text directly on top of the image, such as a headline or quote.",
    action: "Position and style your text overlay, then save",
    readOnly: "Continue without adding an overlay; the image stays without added text.",
    recovery: "If save fails, your overlay stays editable so you can try saving again.",
  },
  'T11.06': {
    body: "Carousel slides let you edit each slide and reorder them by dragging. Saving stores the slide order on the post.",
    action: "Edit and reorder the slides, then save",
    readOnly: "Continue without editing slides now; the current order and content stay as is.",
    recovery: "If save fails, your slide order stays as arranged so you can try saving again.",
  },
  'T11.07': {
    body: "The thread editor lets you edit each post in the thread individually before saving the whole sequence.",
    action: "Edit the thread posts, then save",
    readOnly: "Continue without editing the thread now; the current posts stay as generated.",
    recovery: "If save fails, your edits stay in the editor so you can try saving again.",
  },
  'T11.08': {
    body: "Exporting downloads the selected image, carousel or thread media to your device for use outside ZUPA.",
    action: "Download the selected result",
    readOnly: "Continue without downloading; the media stays available here to export later.",
    recovery: "If the download fails, try again or open the result again before exporting.",
  },
  'T12.01': {
    body: "The library lists every saved group of posts, including older posts carried over from your account before this workspace.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If the library looks empty, create a post first; it appears here once saved.",
  },
  'T12.02': {
    body: "Search and filters narrow the library by keyword, status or platform, and favourites let you mark posts to find again quickly.",
    action: "Search, filter or favourite a post",
    readOnly: "Continue without filtering; the full unfiltered library stays visible.",
    recovery: "If a filter will not apply, clear it and try selecting it again.",
  },
  'T12.03': {
    body: "Collections group related posts together so you can find them as a set, such as everything for one campaign.",
    action: "Add the post to a collection, then save",
    readOnly: "Continue without organising into a collection; the post stays reachable from the library.",
    recovery: "If save fails, the post stays uncategorised and you can try adding it again.",
  },
  'T12.04': {
    body: "Generation history lists past sessions and turns. Reopening one lets you pick up that conversation again instead of starting over.",
    action: "Reopen a past generation session",
    readOnly: "Continue without reopening history; you can start a new generation instead.",
    recovery: "If a session will not reopen, refresh the history list and try again.",
  },
  'T12.05': {
    body: "The prompt library saves prompts you write so you can reuse them later instead of retyping the same idea.",
    action: "Save or reuse a prompt",
    readOnly: "Continue without saving a prompt now; you can add one later.",
    recovery: "If save fails, your prompt text stays in the field so you can try saving again.",
  },
  'T13.01': {
    body: "Content Approvals lists drafts waiting for review and posts already approved. Filters narrow this list by status.",
    action: "Open Approvals and apply a filter",
    readOnly: "Continue without filtering; the full approvals queue stays visible.",
    recovery: "If a filter will not apply, clear it and try selecting it again.",
  },
  'T13.02': {
    body: "Each card shows the facts behind the post, a credibility check and where it will be sent, so you can review before approving.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If a card is missing detail, refresh Approvals to reload it.",
  },
  'T13.03': {
    body: "A drip question appears when ZUPA needs one more piece of company context before it can finish a post confidently.",
    action: "Answer the question, then save",
    readOnly: "Continue without answering now; the question stays pending until you do.",
    recovery: "If save fails, your answer stays in the field so you can try saving again.",
  },
  'T13.04': {
    body: "Approving can trigger an optional AI image cost if the post needs one generated. The guide never approves for you; you press Approve yourself.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If approval fails, the post stays in the queue unapproved and you can retry.",
  },
  'T13.05': {
    body: "Reopening takes an approved item back into editing so you can change it before it goes any further.",
    action: "Reopen the approved item for editing",
    readOnly: "Continue without reopening; the item stays approved as is.",
    recovery: "If reopen fails, the item stays approved and you can try again.",
  },
  'T14.01': {
    body: "The calendar distinguishes three things on the same grid: planned content ideas, personal notes and posts scheduled for delivery.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If the calendar looks empty, that means nothing is planned yet for this range.",
  },
  'T14.02': {
    body: "Saving an idea on a date plans content for later without scheduling or sending anything yet.",
    action: "Save an idea for a date",
    readOnly: "Continue without saving an idea; the date stays open.",
    recovery: "If save fails, your idea text stays in the field so you can try saving again.",
  },
  'T14.03': {
    body: "This dialog previews the destination account, send time and timezone before anything is actually scheduled.",
    action: "Choose the account, time and timezone",
    readOnly: "Continue without setting these; nothing is scheduled without your confirmation.",
    recovery: "If the preview will not load, close and reopen the dialog to try again.",
  },
  'T14.04': {
    body: "Confirming here actually schedules the post for delivery at the chosen time. The guide never confirms for you; you press confirm yourself.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If confirmation fails, nothing is scheduled and you can retry from the dialog.",
  },
  'T14.05': {
    body: "Delivery status shows whether a scheduled post actually sent, failed, or is in an uncertain state that needs your attention.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If a status looks stuck, refresh the calendar to pull the latest delivery result.",
  },
  'T14.06': {
    body: "Cancelling or rescheduling only works before a post has actually gone out; once delivered it cannot be recalled automatically.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If cancel is unavailable, the post has likely already sent; check delivery status instead.",
  },
  'T15.01': {
    body: "These tabs move between sources, confirmed facts and learning: everything ZUPA has gathered and learned about your company.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If a tab will not open, refresh the Company knowledge page and try again.",
  },
  'T15.02': {
    body: "Confirming a fact marks it as accurate so ZUPA can use it confidently in future writing, instead of treating it as unverified.",
    action: "Review the fact, then confirm it",
    readOnly: "Continue without confirming now; the fact stays unconfirmed until you do.",
    recovery: "If save fails, the fact stays pending and you can try confirming it again.",
  },
  'T15.03': {
    body: "This permission controls whether a piece of proof, such as a testimonial, may be used publicly or must stay private.",
    action: "Choose the permission, then save",
    readOnly: "Continue without setting permission now; the proof stays private until you do.",
    recovery: "If save fails, the setting stays unchanged and you can try saving again.",
  },
  'T15.04': {
    body: "Harvested knowledge is information ZUPA picked up automatically. Keeping it adds it to your profile; rejecting it discards it.",
    action: "Review the harvested item, then keep or reject it",
    readOnly: "Continue without resolving it now; it stays pending review.",
    recovery: "If the action fails, the item stays pending and you can try again.",
  },
  'T15.05': {
    body: "A company moment is a useful event or milestone you record yourself, such as a launch, that ZUPA can reference later.",
    action: "Record a company moment, then save",
    readOnly: "Continue without recording a moment now; you can add one later.",
    recovery: "If save fails, your text stays in the field so you can try saving again.",
  },
  'T15.06': {
    body: "A brand rule is a pattern ZUPA has learned from your edits. Accepting it lets ZUPA keep applying it; revoking it stops that.",
    action: "Accept or revoke the rule, then save",
    readOnly: "Continue without deciding now; the rule keeps its current status.",
    recovery: "If save fails, the rule stays unchanged and you can try again.",
  },
  'T15.07': {
    body: "Evidence here is observed directly from your actual edits and posts; it never shows an invented score or improvement figure.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If evidence is missing, that means there is not enough history yet to show any.",
  },
  'T16.01': {
    body: "Uploading metrics or a screenshot brings real performance data into ZUPA so case studies and reviews reference actual numbers.",
    action: "Upload a metrics file or screenshot",
    readOnly: "Continue without uploading now; you can bring in data later from Analytics.",
    recovery: "If the upload fails, try a different file or format and upload again.",
  },
  'T16.02': {
    body: "Mapping tells ZUPA which uploaded column is which metric, and which timezone the timestamps are in, before it is confirmed.",
    action: "Map the columns and timezone, then save",
    readOnly: "Continue without mapping now; the upload stays unconfirmed until you do.",
    recovery: "If save fails, your mapping choices stay on screen so you can try again.",
  },
  'T16.03': {
    body: "Reviewing shows which rows were checked and explains why some metrics are null rather than guessing a number for them.",
    action: "Review the rows, then confirm the batch",
    readOnly: "Continue without confirming now; the batch stays unconfirmed.",
    recovery: "If confirmation fails, the mapping stays saved and you can try confirming again.",
  },
  'T16.04': {
    body: "The case study editor turns your client details, challenge, results and quote into a written case study you can review and reuse.",
    action: "Fill in the case study details, then generate it",
    readOnly: "Continue without generating a case study now; you can start one later.",
    recovery: "If generation fails, your entered details stay in the form so you can try again.",
  },
  'T16.05': {
    body: "From a case study you can explore generating a related campaign or blog post, reusing the same facts and results.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If this panel will not open, refresh the case study and try again.",
  },
  'T17.01': {
    body: "Settings navigation reaches your profile and every preference area: preferences, connections, subscription and account controls.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If a settings section will not open, refresh the page and try the link again.",
  },
  'T17.02': {
    body: "These preferences set your language, timezone and whether interface motion is reduced, applied across the whole app.",
    action: "Set your language, timezone and motion preference, then save",
    readOnly: "Continue without saving preferences now; current defaults stay in effect.",
    recovery: "If save fails, your selections stay on screen so you can try saving again.",
  },
  'T17.03': {
    body: "These settings choose which reminders and weekly summaries ZUPA sends you by notification or email.",
    action: "Choose your reminders and summaries, then save",
    readOnly: "Continue without changing notification settings; current settings stay in effect.",
    recovery: "If save fails, your selections stay on screen so you can try saving again.",
  },
  'T17.04': {
    body: "This shows which social accounts are connected and their identity. Reconnecting only happens when you choose it; the guide never starts it for you.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If a connection shows disconnected, use its own reconnect control when you are ready.",
  },
  'T17.05': {
    body: "This summary shows your current plan tier, usage against it, and whether you are on a trial or complimentary access.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If usage numbers look wrong, refresh the page to pull the latest totals.",
  },
  'T17.06': {
    body: "Renewal options are handled by your payment provider. The guide never changes or confirms a renewal for you; you do that yourself.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If renewal details will not load, refresh this page and check again.",
  },
  'T17.07': {
    body: "Export and deletion are permanent account actions. The guide only explains them; it never exports or deletes anything for you.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If the export or delete control seems unresponsive, refresh the page before trying it yourself.",
  },
  'T18.01': {
    body: "This summary lists which chapters you actually practiced and which you skipped, so you know exactly what you have covered.",
    action: "",
    readOnly: "Read this, then press Next.",
    recovery: "If the summary looks incomplete, reopen the guide from here to update it.",
  },
  'T18.02': {
    body: "The chapter list lets you jump back into any part of the guide to replay it, whether you completed it or skipped it before.",
    action: "Select a chapter to replay",
    readOnly: "Continue without replaying a chapter now; you can return here any time.",
    recovery: "If a chapter will not open, refresh the guide and select it again.",
  },
  'T18.03': {
    body: "Pausing the guide saves your place so you can pick up exactly where you left off later, from any device you sign into.",
    action: "Pause the guide, then resume it later",
    readOnly: "Continue without pausing now; you can pause from here at any point.",
    recovery: "If resume does not return you to the right step, reopen the chapter list and pick the step directly.",
  },
}

export const SUBANCHOR_COPY: Record<string, string> = {
  'case-study-new': "Start a new case study from scratch instead of opening one you already created.",
  'case-study-history': "Browse case studies you already generated and open one to review or continue editing.",
  'case-study-client': "Enter the client or project name this case study is about.",
  'case-study-context': "Describe the background and context needed to understand the case study.",
  'case-study-challenges-solution': "Describe the challenge the client faced and the solution your product or service provided.",
  'case-study-metrics-quote': "Add the real result metrics and a client quote to support the case study.",
  'case-study-notes-keywords': "Add extra notes and keywords ZUPA should weave into the generated writing.",
  'case-study-generate': "Generate the case study text from the details you entered; you press this yourself.",
  'case-study-document': "Review or download the generated case study as a document.",
  'case-study-blog': "Explore turning this case study into a blog post.",
  'case-study-copy': "Explore generating supporting marketing copy from this case study.",
  'case-study-campaign': "Explore generating a campaign built around this case study.",
  'case-study-campaign-entries': "Review the individual entries generated for this campaign.",
  'case-study-planned-calendar': "See how campaign entries would fit onto the content calendar.",
  'case-study-open-studio': "Open a generated entry in Studio to edit it further.",
}

/** Copy for the returning-user branch of T01.01–T01.03 (guide-owned account status card) and T02.01–T02.02 (/settings/workspaces). */
export const RETURNING_COPY: Record<string, string> = {
  'T01.01': "Since you are already signed in, this card just shows your account status; it does not sign you out or change anything.",
  'T01.02': "Since your email is already verified, this card shows that status read-only; it will not ask you to reverify.",
  'T01.03': "Since you already confirmed your privacy choices, this card shows them read-only without resetting your consent.",
  'T02.01': "Since you already have a workspace, this opens Settings > Workspaces to review it instead of creating a duplicate.",
  'T02.02': "Since you already have an active workspace, this opens Settings > Workspaces so you can switch without duplicating anything.",
}

/** Copy for first_run_branch steps shown on /onboarding before setup is finished. */
export const FIRST_RUN_COPY: Record<string, string> = {
  'T03.01': "Since onboarding is not finished, this step opens the source step inside onboarding instead of the guarded Company page.",
  'T03.02': "Since onboarding is not finished, this step opens the research progress view inside onboarding instead of Company.",
  'T03.03': "Since onboarding is not finished, this step opens the proposed-changes view inside onboarding instead of Company.",
  'T03.04': "Since onboarding is not finished, this step opens the apply-selection view inside onboarding instead of Company.",
  'T04.01': "Since onboarding is not finished, this step opens the product list inside onboarding instead of the guarded Company page.",
  'T04.02': "Since onboarding is not finished, this step opens the product form inside onboarding instead of the guarded Company page.",
  'T04.03': "Since onboarding is not finished, this step opens the persona list inside onboarding instead of the guarded Company page.",
  'T04.04': "Since onboarding is not finished, this step opens the persona form inside onboarding instead of the guarded Company page.",
  'T05.01': "Since onboarding is not finished, this step opens the brand voice form inside onboarding instead of Company.",
  'T05.02': "Since onboarding is not finished, this step opens the visual brand form inside onboarding instead of Company.",
  'T05.03': "Since onboarding is not finished, this step opens the brand save check inside onboarding instead of Company.",
  'T06.01': "Since onboarding is not finished, this step opens the goals form inside onboarding instead of the guarded Company page.",
  'T06.02': "Since onboarding is not finished, this step opens the cadence control inside onboarding instead of Company.",
  'T06.03': "Since onboarding is not finished, this step opens the Autopilot explanation inside onboarding instead of Company.",
}

export const EMPTY_STATE_COPY: Record<string, string> = {
  T09: "Create a post first; results appear here once you have generated one.",
  T10: "Create a post first; Studio opens once you have a draft to edit.",
  T11: "Create a post with an image, carousel or thread first; media tools open once you have one.",
  T12: "Create a post first; the library fills in once you have saved content.",
  T13: "Create and save a post first; Approvals fills in once something is waiting for review.",
  T14: "Schedule or plan a post first; the calendar fills in once something is placed on it.",
  T16: "Import analytics data or create a case study first; this fills in once you have one.",
}

export const RESTRICTED_COPY = "Not in your plan"

